import { t, type I18nLocale } from '@ardoq/i18n';
import type {
  DeckRow,
  PlaceholderReason,
  SlideContent,
  SlideImage,
} from './slidePlan';

type Box = { x: number; y: number; w: number; h: number };

type Size = { w: number; h: number };

export type DeckSlide = {
  title: string;
  notes: string;
  titleBox: Box;
  body:
    | { kind: 'image'; data: string; box: Box }
    | { kind: 'placeholder'; text: string; box: Box };
};

type DeckSlidesArgs = {
  row: DeckRow;
  index: number;
  i18nLocale: I18nLocale;
};

type PartTitleArgs = {
  title: string;
  part: number;
  total: number;
  i18nLocale: I18nLocale;
};

type BuildDeckArgs = {
  rows: DeckRow[];
  presentationName: string | undefined;
  i18nLocale: I18nLocale;
};

const PX_PER_INCH = 96;
const TITLE_BOX_PX: Box = { x: 40, y: 24, w: 1200, h: 56 };
export const IMAGE_BOX_PX: Box = { x: 40, y: 110, w: 1200, h: 560 };

const toInches = (px: number): number =>
  Math.round((px / PX_PER_INCH) * 1000) / 1000;

const boxToInches = ({ x, y, w, h }: Box): Box => ({
  x: toInches(x),
  y: toInches(y),
  w: toInches(w),
  h: toInches(h),
});

const fitContain = (image: Size, box: Box): Box => {
  const scale = Math.min(box.w / image.w, box.h / image.h);
  const w = image.w * scale;
  const h = image.h * scale;
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
};

const toReasonText = (
  reason: PlaceholderReason,
  i18nLocale: I18nLocale
): string => {
  switch (reason) {
    case 'unauthorized':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.unauthorized',
        "You don't have access to this slide"
      );
    case 'workspace-mode':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.workspaceMode',
        'Workspace mode slides are not exportable. Please migrate to viewpoint mode slide'
      );
    case 'scenario-missing':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.scenarioMissing',
        "This slide's scenario no longer exists"
      );
    case 'not-exportable-view':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.notExportableView',
        "This view can't be exported"
      );
    case 'type-not-supported':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.typeNotSupported',
        "This slide type can't be exported yet"
      );
    case 'failed':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.failed',
        'The image export failed'
      );
    case 'slide-load-failed':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.slideLoadFailed',
        'The slide failed to load'
      );
    case 'not-ready':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.notReady',
        "The slide didn't finish loading in time"
      );
    case 'no-exporter':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.noExporter',
        "This view can't be exported as an image"
      );
    case 'timeout':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.timeout',
        'The image export timed out'
      );
    case 'busy':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.busy',
        'Skipped because a previous image export was still running'
      );
    case 'no-image':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.noImage',
        'The image export produced no image'
      );
    case 'too-large':
      return t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.placeholder.tooLarge',
        'This slide is too large to export as an image'
      );
  }
};

const toImageBody = ({ dataUrl, w, h }: SlideImage): DeckSlide['body'] => ({
  kind: 'image',
  data: dataUrl,
  box: boxToInches(fitContain({ w, h }, IMAGE_BOX_PX)),
});

const toBodies = (
  content: SlideContent,
  i18nLocale: I18nLocale
): DeckSlide['body'][] => {
  switch (content.kind) {
    case 'image':
      return content.images.map(toImageBody);
    case 'placeholder':
      return [
        {
          kind: 'placeholder',
          text: toReasonText(content.reason, i18nLocale),
          box: boxToInches(IMAGE_BOX_PX),
        },
      ];
  }
};

const toDefaultPresentationName = (i18nLocale: I18nLocale): string =>
  t(
    i18nLocale,
    'presentationAdmin:viewer.defaultPresentationName',
    'Presentation'
  );

const toPartTitle = ({
  title,
  part,
  total,
  i18nLocale,
}: PartTitleArgs): string =>
  total > 1
    ? t(
        i18nLocale,
        'presentationAdmin:viewer.pptxExport.slidePartTitle',
        { title, part, total },
        '{{title}} ({{part}}/{{total}})'
      )
    : title;

export const toDeckSlides = ({
  row: { title, description, content },
  index,
  i18nLocale,
}: DeckSlidesArgs): DeckSlide[] => {
  const slideTitle =
    title ||
    t(
      i18nLocale,
      'presentationAdmin:viewer.slideNavigator.untitledSlide',
      { number: index + 1 },
      'Slide {{number}}'
    );
  const bodies = toBodies(content, i18nLocale);
  return bodies.map((body, part) => ({
    title: toPartTitle({
      title: slideTitle,
      part: part + 1,
      total: bodies.length,
      i18nLocale,
    }),
    notes: part === 0 ? description : '',
    titleBox: boxToInches(TITLE_BOX_PX),
    body,
  }));
};

export const toDeckFileName = (
  presentationName: string | undefined,
  i18nLocale: I18nLocale
): string => {
  const cleaned = (presentationName ?? '')
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `${cleaned || toDefaultPresentationName(i18nLocale)}.pptx`;
};

export const buildAndSaveDeck = async ({
  rows,
  presentationName,
  i18nLocale,
}: BuildDeckArgs): Promise<void> => {
  const { default: PptxGenJS } = await import('pptxgenjs');
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.title = presentationName || toDefaultPresentationName(i18nLocale);

  rows
    .flatMap((row, index) => toDeckSlides({ row, index, i18nLocale }))
    .forEach(({ title, notes, titleBox, body }) => {
      const slide = pptx.addSlide();
      slide.addText(title, {
        ...titleBox,
        fontSize: 24,
        bold: true,
        color: '333333',
        valign: 'middle',
        fit: 'shrink',
      });
      if (body.kind === 'image') {
        slide.addImage({ data: body.data, ...body.box });
      } else {
        slide.addText(body.text, {
          ...body.box,
          fontSize: 20,
          color: '999999',
          align: 'center',
          valign: 'middle',
        });
      }
      if (notes) {
        slide.addNotes(notes);
      }
    });

  await pptx.writeFile({
    fileName: toDeckFileName(presentationName, i18nLocale),
    compression: true,
  });
};
